import React, { useState, useMemo } from 'react';
import { LocalAtRecord } from '../../../db';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { HeatmapAttribute, HeatmapAttributeCalculator } from './domain/HeatmapAttribute';
import { IconButton } from '@mui/material';
import { Tooltip } from '@mui/material';
import { MdBarChart, MdGradient } from 'react-icons/md';

interface HeatmapHistogramProps {
  jobs: LocalAtRecord<JobsFieldSet>[];
  attribute: HeatmapAttribute;
  gradient: string[];
  getPullinFromJob: (job: LocalAtRecord<JobsFieldSet>) => [number, number] | undefined;
}

type VisualizationType = 'gradient' | 'histogram';

export function HeatmapHistogram({ jobs, attribute, gradient, getPullinFromJob }: HeatmapHistogramProps) {
  const [visualizationType, setVisualizationType] = useState<VisualizationType>('gradient');

  // Calculate data points and statistics
  const statistics = useMemo(() => {
    const dataPoints = HeatmapAttributeCalculator.createDataPoints(jobs, attribute, getPullinFromJob);

    if (dataPoints.length === 0) {
      return {
        isEmpty: true,
        minValue: 0,
        maxValue: 0,
        bins: [],
        binEdges: [],
        config: HeatmapAttributeCalculator.getConfig(attribute),
      };
    }

    // Extract raw values from metadata
    const rawValues = dataPoints.map((point) => point.metadata.rawValue as number);
    const minValue = Math.min(...rawValues);
    const maxValue = Math.max(...rawValues);

    // Check if all values are the same
    if (minValue === maxValue) {
      return {
        isEmpty: true,
        minValue,
        maxValue,
        bins: [],
        binEdges: [],
        config: HeatmapAttributeCalculator.getConfig(attribute),
      };
    }

    // Calculate percentile-based bin edges for more even distribution
    const numBins = gradient.length;
    const binEdges = HeatmapAttributeCalculator.calculateBinEdges(rawValues, numBins);

    // Create bins based on percentile ranges
    const bins = Array.from({ length: numBins }, (_, index) => ({
      count: 0,
      minValue: binEdges[index],
      maxValue: binEdges[index + 1],
      color: gradient[index],
    }));

    // Distribute data points into bins based on their raw values
    dataPoints.forEach((point) => {
      const rawValue = point.metadata.rawValue as number;

      // Find which bin this value belongs to
      let binIndex = 0;

      for (let i = 0; i < numBins; i++) {
        if (rawValue >= binEdges[i] && rawValue <= binEdges[i + 1]) {
          binIndex = i;
          break;
        }
      }

      // Handle edge case where value equals max
      if (rawValue === binEdges[numBins]) {
        binIndex = numBins - 1;
      }

      bins[binIndex].count++;
    });

    return {
      isEmpty: false,
      minValue,
      maxValue,
      bins,
      binEdges,
      config: HeatmapAttributeCalculator.getConfig(attribute),
    };
  }, [jobs, attribute, gradient, getPullinFromJob]);

  const toggleVisualization = () => {
    setVisualizationType((prev) => (prev === 'gradient' ? 'histogram' : 'gradient'));
  };

  if (statistics.isEmpty) {
    return (
      <div
        style={{
          padding: '10px',
          marginTop: '10px',
          borderTop: '1px solid #e0e0e0',
          color: '#666',
          fontSize: '12px',
          fontStyle: 'italic',
        }}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '10px',
        marginTop: '10px',
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
          {statistics.config.label} Distribution
        </div>
        <Tooltip title={visualizationType === 'gradient' ? 'Show histogram' : 'Show gradient'}>
          <IconButton size="small" onClick={toggleVisualization} style={{ padding: '4px' }}>
            {visualizationType === 'gradient' ? <MdBarChart size={16} /> : <MdGradient size={16} />}
          </IconButton>
        </Tooltip>
      </div>

      {visualizationType === 'gradient' ? (
        <GradientVisualization
          gradient={gradient}
          minValue={statistics.minValue}
          maxValue={statistics.maxValue}
          bins={statistics.bins}
          binEdges={statistics.binEdges}
        />
      ) : (
        <HistogramVisualization bins={statistics.bins} />
      )}
    </div>
  );
}

// Gradient Scale Visualization
function GradientVisualization({
  gradient,
  minValue,
  maxValue,
  bins,
  binEdges,
}: {
  gradient: string[];
  minValue: number;
  maxValue: number;
  bins: Array<{ minValue: number; maxValue: number; color: string; count: number }>;
  binEdges: number[];
}) {
  const gradientString = `linear-gradient(to right, ${gradient.join(', ')})`;

  return (
    <div>
      {/* Color bar */}
      <div
        style={{
          height: '20px',
          background: gradientString,
          borderRadius: '4px',
          marginBottom: '8px',
          border: '1px solid #ddd',
        }}
      />

      {/* Percentile-based value labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666' }}>
        <span title="Minimum">{minValue.toFixed(1)}</span>
        <span title="Median">
          {binEdges[Math.floor(binEdges.length / 2)]?.toFixed(1) || ((minValue + maxValue) / 2).toFixed(1)}
        </span>
        <span title="Maximum">{maxValue.toFixed(1)}</span>
      </div>

      {/* Job counts per bin */}
      <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
          {bins.map((bin, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: bin.color,
                  border: '1px solid #ddd',
                  borderRadius: '2px',
                }}
              />
              <span title={`Range: ${bin.minValue.toFixed(1)} to ${bin.maxValue.toFixed(1)}`}>{bin.count} jobs</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Histogram Visualization
function HistogramVisualization({
  bins,
}: {
  bins: Array<{ minValue: number; maxValue: number; color: string; count: number }>;
}) {
  const maxCount = Math.max(...bins.map((b) => b.count), 1); // Ensure at least 1 to avoid division by zero
  const chartHeight = 100;
  const barWidth = 100 / bins.length - 2; // Percentage width with gap

  return (
    <div>
      {/* SVG Histogram */}
      <svg width="100%" height={chartHeight + 40} style={{ overflow: 'visible' }}>
        {bins.map((bin, index) => {
          const barHeight = maxCount > 0 ? (bin.count / maxCount) * chartHeight : 0;
          const x = (index / bins.length) * 100;

          return (
            <g key={index}>
              {/* Bar */}
              <rect
                x={`${x}%`}
                y={chartHeight - barHeight}
                width={`${barWidth}%`}
                height={barHeight}
                fill={bin.color}
                stroke="#333"
                strokeWidth="1"
              />
              {/* Count label */}
              {bin.count > 0 && (
                <text
                  x={`${x + barWidth / 2}%`}
                  y={chartHeight - barHeight - 5}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#333"
                  fontWeight="bold"
                >
                  {bin.count}
                </text>
              )}
              {/* Value range label */}
              <text x={`${x + barWidth / 2}%`} y={chartHeight + 15} textAnchor="middle" fontSize="9" fill="#666">
                {bin.minValue.toFixed(1)}
              </text>
              <text x={`${x + barWidth / 2}%`} y={chartHeight + 27} textAnchor="middle" fontSize="9" fill="#666">
                {bin.maxValue.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
